// Relais entre deux squelettes : le squelette instantané de la nav et celui que la page
// d'arrivée rend à sa place, la coquille d'une fiche et la réponse `load=true` qui la remplace,
// l'overlay d'une étape de parcours et le moule que pose le turbo stream d'avancement.
//
// Chaque relais remplace des nœuds par d'autres, dessinés à l'identique. Mais une animation CSS
// démarre avec son élément : les nouveaux repartaient de zéro. En thème classique, ça se lisait
// comme un clignement — la matière retombait à 25 % d'opacité (`skeleton-material-in`) et le
// miroitement sautait en début de course. Avec les skins du mode fun, dont les squelettes
// racontent quelque chose (le stylo du cahier Seyès, le curseur du terminal, la barre de copie de
// XP), c'était tout ce qui était déjà « écrit » qui s'effaçait d'un coup au changement d'URL.
//
// Un squelette qui en remplace un autre en reprend donc le fil : ses boucles adoptent l'heure de
// départ de celles qu'il remplace (`startTime`), et son arrivée est tenue pour déjà jouée. Un
// squelette qui arrive sur du contenu réel n'est pas concerné — son arrivée, elle, est voulue.
//
// Toujours DANS le rendu, jamais après : Turbo insère pages, frames et streams depuis un
// requestAnimationFrame (cf. `nextRepaint`), et le moindre report tomberait après un premier
// paint — c'est-à-dire exactement l'image qu'on cherche à ne pas montrer.

// Les éclats du vortex de masquerade empruntent la matière, pas le squelette.
const LOADING_SELECTOR = '.is-loading:not(.masquerade-vortex__shard)';
// L'arrivée de la matière (config/_motion.scss). Nommée plutôt que déduite : c'est la seule
// animation finie d'un squelette, et la seule à ne pas rejouer.
const ARRIVAL_ANIMATION = 'skeleton-material-in';

type RenderFunction = (...args: Element[]) => unknown;

const loadingElements = (root: ParentNode): Element[] => {
  const elements = Array.from(root.querySelectorAll(LOADING_SELECTOR));
  if (root instanceof Element && root.matches(LOADING_SELECTOR)) elements.unshift(root);
  return elements;
};

const cssAnimations = (element: Element) =>
  element.getAnimations().filter((animation): animation is CSSAnimation => animation instanceof CSSAnimation);

const isLoop = (animation: Animation) => animation.effect?.getComputedTiming().iterations === Infinity;

// L'heure de départ des boucles du squelette affiché dans `root`, ou `null` s'il n'y en a pas.
export function readSkeletonClock(root: ParentNode | null | undefined): number | null {
  if (!root) return null;

  for (const element of loadingElements(root)) {
    const loop = cssAnimations(element).find(isLoop);
    if (!loop) continue;

    if (typeof loop.startTime === 'number') return loop.startTime;

    // Encore en attente de sa première image : elle part maintenant.
    const now = document.timeline.currentTime;
    const elapsed = typeof loop.currentTime === 'number' ? loop.currentTime : 0;
    return typeof now === 'number' ? now - elapsed : null;
  }

  return null;
}

// Les squelettes de `root` reprennent là où en était celui qu'ils remplacent.
export function continueSkeletons(root: ParentNode | null | undefined, clock: number | null): void {
  if (!root || clock === null) return;

  loadingElements(root).forEach((element) => {
    cssAnimations(element).forEach((animation) => {
      if (isLoop(animation)) animation.startTime = clock;
      else if (animation.animationName === ARRIVAL_ANIMATION) animation.finish();
    });
  });
}

// Emballe `event.detail.render` (frames et streams) : c'est le seul point où l'on tient à la
// fois l'avant — l'ancien contenu est encore là — et l'après, dans la même tâche que le
// remplacement. `before` lit l'état juste avant, `after` le reprend juste après.
//
// Le rendu d'origine est synchrone pour une frame, asynchrone pour un stream, et un autre
// module a pu l'emballer avant nous (cf. value_change_motion.ts) : on suit sa nature.
const wrapRender = <T>(
  detail: { render?: RenderFunction } | undefined,
  before: () => T,
  after: (state: T) => void
) => {
  const originalRender = detail?.render;
  if (!detail || typeof originalRender !== 'function') return;

  detail.render = (...args: Element[]) => {
    const state = before();
    const rendered = originalRender(...args);
    if (rendered instanceof Promise) return rendered.then(() => after(state));

    after(state);
    return rendered;
  };
};

export function initializeSkeletonHandoff() {
  if ((window as any).__skeletonHandoffInitialized) return;
  (window as any).__skeletonHandoffInitialized = true;

  // Une frame qui affichait un squelette et en reçoit un autre : la coquille d'une fiche, puis la
  // réponse `load=true` et ses sections encore en attente ; la coquille d'un index, puis ses
  // lignes et leurs cellules de prix.
  document.addEventListener('turbo:before-frame-render', (event) => {
    const frame = event.target;
    if (!(frame instanceof Element)) return;

    wrapRender(
      (event as CustomEvent).detail,
      () => readSkeletonClock(frame),
      (clock) => continueSkeletons(frame, clock)
    );
  });

  // Le turbo stream d'avancement d'un parcours remplace la frame `step_form_*` — l'overlay peint
  // au clic sur « Suivant » compris — par une frame neuve qui porte le même moule
  // (cf. workflow_step_skeleton.ts et _turbo_step_advance.turbo_stream.erb).
  document.addEventListener('turbo:before-stream-render', (event) => {
    const detail = (event as CustomEvent).detail;
    const stream = detail?.newStream as (Element & { targetElements?: Element[] }) | undefined;
    const action = stream?.getAttribute('action');
    if (action !== 'replace' && action !== 'update') return;

    const targets = () => {
      try {
        return stream?.targetElements ?? [];
      } catch {
        return []; // stream sans cible : Turbo le signalera lui-même
      }
    };

    wrapRender(
      detail,
      () => {
        const current = targets();
        const clock = current.map((target) => readSkeletonClock(target)).find((value) => value !== null) ?? null;
        return { clock, current, ids: current.map((target) => target.id).filter(Boolean) };
      },
      ({ clock, current, ids }) => {
        if (clock === null) return;

        // `replace` a posé un nœud neuf, qu'on retrouve par son id ; `update` a gardé la cible.
        const rendered = action === 'replace' ? ids.map((id) => document.getElementById(id)) : current;
        rendered.forEach((element) => continueSkeletons(element, clock));
      }
    );
  });
}
